import type { NoteKind } from '@goalbuddy/core';
import { Markdown } from '@tiptap/markdown';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useMutation, useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { fmtDateTime } from '@/lib/utils';

/** Markdown note with version history. Saves create a new version; a stale base version gets a 409. */
export function NoteEditor({
  nodeId,
  kind,
  readOnly,
}: {
  nodeId: number;
  kind: NoteKind;
  readOnly?: boolean;
}) {
  const { data: note, refetch } = useQuery({
    queryKey: ['note', nodeId, kind],
    queryFn: () => api.note(nodeId, kind),
  });
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null);
  const [showVersions, setShowVersions] = useState(false);
  const baseVersion = useRef(0);

  const editor = useEditor({
    extensions: [StarterKit, Markdown],
    editable: !readOnly,
    content: '',
    contentType: 'markdown',
    editorProps: { attributes: { class: 'prose-editor' } },
    onUpdate: () => setDirty(true),
  });

  useEffect(() => {
    if (!editor || !note || dirty) return;
    baseVersion.current = note.version;
    editor.commands.setContent(note.body_md, { contentType: 'markdown', emitUpdate: false });
  }, [editor, note, dirty]);

  const save = useMutation({
    mutationFn: () => api.putNote(nodeId, kind, editor!.getMarkdown(), baseVersion.current),
    onSuccess: (n) => {
      baseVersion.current = n.version;
      setDirty(false);
      setConflict(null);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setConflict(
          'Someone else saved this note in the meantime. Copy your text, reload, and merge your changes.',
        );
      }
    },
  });

  const versions = useQuery({
    queryKey: ['note-versions', nodeId, kind],
    queryFn: () => api.noteVersions(nodeId, kind),
    enabled: showVersions,
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-muted-fg">
        <span>
          {note?.version ? `Version ${note.version} · ${fmtDateTime(note.updated_at)}` : 'Empty'}
          {readOnly && ' · read-only (written by agents)'}
        </span>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => setShowVersions((v) => !v)}>
            <History size={13} /> History
          </Button>
          {!readOnly && (
            <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : dirty ? 'Save' : 'Saved'}
            </Button>
          )}
        </div>
      </div>
      {conflict && (
        <div className="rounded-lg border border-danger/40 bg-danger/5 p-2 text-sm text-danger">
          {conflict}{' '}
          <Button
            variant="link"
            size="sm"
            onClick={async () => {
              setDirty(false);
              setConflict(null);
              await refetch();
            }}
          >
            Reload latest
          </Button>
        </div>
      )}
      <div
        className="rounded-xl border border-border bg-surface p-4 focus-within:border-primary/50"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 's') {
            e.preventDefault();
            if (dirty && !save.isPending) save.mutate();
          }
        }}
      >
        <EditorContent editor={editor} />
      </div>
      {showVersions && (
        <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border border-border p-2">
          {versions.data?.length === 0 && <div className="text-xs text-muted-fg">No versions yet.</div>}
          {versions.data?.map((v) => (
            <details key={v.id} className="text-xs">
              <summary className="cursor-pointer">
                v{v.version} · {fmtDateTime(v.created_at)}
              </summary>
              <pre className="mt-1 whitespace-pre-wrap rounded bg-muted p-2">{v.body_md}</pre>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}
